import { useState } from 'react';
import {
  Building,
  Calendar,
  ExternalLink,
  ReferenceIcon,
  Signer,
} from 'assets/icons';
import BadgeSquare from 'components/badge/badgeSquare';
import { MISSION_STATUS_BADGE_TYPE } from 'constants/missionStatusBadge';
import StyledInput from 'components/styledInput/StyledInput';
import TempWorkerMission from 'components/tempWorkerMission/tempWorkerMission';
import { dateWordShort } from 'helper/helper';
import { highlightSlot } from 'utils/searchResultHighlight';

/**
 * A mission, presented.
 *
 * The list and the contract preview showed the same mission and drew it twice,
 * so the preview ended up with a temp worker written out by hand while the list
 * had a card for it, and with a company line that told you less.
 *
 * What belongs to the list rather than to the mission — the bulk checkbox, the
 * assignment dropdown, the link to copy — is a slot or a callback: given none,
 * the card is a plain read of the mission.
 */

const INACTIVE_STATUSES = ['suspended', 'canceled', 'interrupted', 'deleted'];

const DEFAULT_DETAILS = ['period', 'company', 'contract', 'reference'];

const MissionCard = ({
  mission,
  /** Search presentation: which terms to highlight, and why it matched. */
  match,
  /** The yellow border of the mission being read in the list. */
  selected = false,
  onClick,
  /** The bulk-selection checkbox: `{ checked, onChange }`. */
  selection,
  /** Anything the list puts at the right of the header. */
  actions,
  /** Whose card fills the other half — the list has it under another name. */
  tempWorker,
  /** Called with nothing: which id opens a panel depends on where the mission
      was read, and the card is not the one that knows. */
  onOpenCompany,
  onOpenTempWorker,
  /** Which lines to draw, in the card's own order. */
  details = DEFAULT_DETAILS,
  /** 'stacked' when the card sits in a column too narrow to halve. */
  layout = 'row',
}) => {
  // Local: the icon shows on the line being pointed at, which nothing outside
  // the card needs to know.
  const [companyHovered, setCompanyHovered] = useState(false);

  const company = mission?.company;
  const companyLabel =
    company?.reason_social === company?.name_commercial
      ? company?.reason_social
      : `${company?.reason_social} => ${company?.name_commercial}`;
  const companyText = `${companyLabel || ''}${
    company?.id_bestt ? ` n°${company.id_bestt}` : ''
  }`;
  const missionIdLabel = `n°${mission?.id_mission}`;
  const contractLabel = mission?.contract_tempworker?.[0]?.id_bestt
    ? `Contrat n°${mission.contract_tempworker[0].id_bestt}`
    : 'Aucun contrat BeSTT';
  const referenceLabel = mission?.reference
    ? `Référence n°${mission.reference}`
    : null;

  const shows = (line) => details.includes(line);
  const isInactive = INACTIVE_STATUSES.includes(mission?.status);
  const statusBadgeType = MISSION_STATUS_BADGE_TYPE[mission?.status];
  const isStacked = layout === 'stacked';

  return (
    <div
      // The card carries its own size and colour: dropped in a modal, whose
      // body sets text-sm text-gray-600, it read smaller and paler than the
      // same card in the list — which inherited the page's plain black.
      className={`ft-b text-gray-900 flex flex-col gap-1.5 ${
        selected ? 'border border-yellow-500' : 'border border-gray-100'
      } rounded-lg p-4 ${
        onClick ? 'cursor-pointer hover:bg-gray-50' : ''
      } group mission-container-body-list-item`}
      onClick={
        onClick
          ? (e) => {
              e.preventDefault();
              e.stopPropagation();
              onClick();
            }
          : undefined
      }
    >
      <div className="flex gap-1.5 justify-between items-center">
        {selection && (
          <div
            className={`${
              selection.checked ? 'block' : 'hidden group-hover:block'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <StyledInput
              type="checkbox"
              checked={selection.checked}
              onChange={(e) => selection.onChange(e.target.checked)}
            />
          </div>
        )}
        <div className="flex items-center gap-1 flex-1 min-w-0 [&_.search-highlight]:font-bold [&_.search-highlight]:bg-yellow-50">
          <div className="truncate">{mission?.title}</div>
          <span>
            {highlightSlot(missionIdLabel, match?.slotTerms, 'missionId')}
          </span>
        </div>
        <div className="flex gap-1.5 items-center text-xs mission-container-body-list-item-actions">
          {!isInactive && mission?.is_urgent && (
            <BadgeSquare type={11} size="12" />
          )}
          {statusBadgeType && <BadgeSquare type={statusBadgeType} size="12" />}
          {actions}
        </div>
      </div>

      {/* Date range, company, contract, reference */}
      <div
        className={`text-sm flex ${
          isStacked ? 'flex-col gap-2' : 'items-center justify-between gap-4'
        }`}
      >
        <div
          className={`flex flex-col gap-0.5 text-gray-700 ${
            isStacked ? 'w-full' : 'w-1/2'
          } [&_.search-highlight]:text-gray-900 [&_.search-highlight]:font-bold [&_.search-highlight]:bg-yellow-50`}
        >
          {shows('period') && mission?.duration?.datestart && (
            <div className="flex gap-1 items-center">
              <div>
                <Calendar wh={18} color="#374151" />
              </div>
              {dateWordShort(mission.duration.datestart) +
                ' > ' +
                dateWordShort(mission.duration.dateend)}
            </div>
          )}
          {shows('company') && company && (
            <div className="flex gap-1 items-center">
              <div>
                <Building wh={18} color="#374151" />
              </div>
              <span
                className={`inline-flex gap-1 items-center ${
                  onOpenCompany ? 'cursor-pointer' : ''
                }`}
                onMouseEnter={() => onOpenCompany && setCompanyHovered(true)}
                onMouseLeave={() => onOpenCompany && setCompanyHovered(false)}
                onClick={
                  onOpenCompany
                    ? (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onOpenCompany();
                      }
                    : undefined
                }
              >
                {highlightSlot(companyText, match?.slotTerms, 'company')}
                {companyHovered && <ExternalLink wh={18} color="#235FE6" />}
              </span>
            </div>
          )}
          {shows('contract') && tempWorker && (
            <div className="flex gap-1 items-center">
              <div>
                <Signer wh={18} color="#374151" />
              </div>
              <div className="flex flex-col">
                <span className="inline">
                  {highlightSlot(contractLabel, match?.slotTerms, 'contract')}
                </span>
                {mission?.contract_tempworker
                  ?.filter((c) => (c.avenant_number || 0) > 0)
                  .map((c) => (
                    <span
                      key={c.avenant_number}
                      className="text-gray-500 text-xs"
                    >
                      Avenant n°{c.id_bestt || c.avenant_number}
                    </span>
                  ))}
              </div>
            </div>
          )}
          {shows('reference') && referenceLabel && (
            <div className="flex gap-1 items-center">
              <div>
                <ReferenceIcon wh={18} color="#374151" />
              </div>
              <span>
                {highlightSlot(referenceLabel, match?.slotTerms, 'reference')}
              </span>
            </div>
          )}
          {match?.matchHints?.map((hint) => (
            <div key={hint} className="text-xs italic text-gray-500">
              {hint}
            </div>
          ))}
        </div>

        {tempWorker && (
          <div
            className={`${
              isStacked ? 'w-full' : 'w-1/2'
            } rounded-lg border border-gray-100 px-4 py-2.5 text-gray-500 ${
              onOpenTempWorker ? 'cursor-pointer hover:bg-gray-100' : ''
            }`}
            onClick={
              onOpenTempWorker
                ? (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenTempWorker();
                  }
                : undefined
            }
          >
            <TempWorkerMission
              user={tempWorker}
              slotTerms={match?.slotTerms}
              searchTerms={match?.terms}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MissionCard;
